import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import { createGoogleIntegrationApi } from '../services/googleIntegrationApi';
import { GOOGLE_SERVICES, GoogleAccount } from '../types/googleIntegration';

export const GoogleIntegrationPage: React.FC = () => {
  const { api } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState<GoogleAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [connectingService, setConnectingService] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const googleApi = createGoogleIntegrationApi(api);
      const userAccounts = await googleApi.getAccounts();
      setAccounts(userAccounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Google accounts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Handle redirect parameters from OAuth callback
  useEffect(() => {
    const success = searchParams.get('success');
    const errorParam = searchParams.get('error');
    const message = searchParams.get('message');
    const email = searchParams.get('email');
    const service = searchParams.get('service');

    if (success === 'true') {
      // Reload accounts to show newly connected account
      loadAccounts();

      // Show success message
      if (email && service) {
        setSuccessMessage(`Successfully connected ${decodeURIComponent(email)} (${decodeURIComponent(service)})`);
      } else {
        setSuccessMessage('Google account successfully connected');
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      // Remove success parameters from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('success');
      newParams.delete('email');
      newParams.delete('service');
      setSearchParams(newParams, { replace: true });
    }

    if (errorParam === 'true') {
      // Show error message
      if (message) {
        setError(decodeURIComponent(message));
      } else {
        setError('Failed to connect Google account');
      }

      // Remove error parameters from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      newParams.delete('message');
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleConnect = async (serviceName: string) => {
    try {
      setConnectingService(serviceName);
      setError(null);

      // Get the authorization URL
      const googleApi = createGoogleIntegrationApi(api);
      const authUrl = googleApi.getAuthUrl(serviceName);

      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Google authorization');
      setConnectingService(null);
    }
  };

  const handleDisconnect = async (accountId: number, email: string, service: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to disconnect ${email} (${service})?`)) {
      return;
    }

    try {
      setError(null);
      const googleApi = createGoogleIntegrationApi(api);
      await googleApi.deleteAccount(accountId);

      // Remove from local state
      setAccounts(accounts.filter(account => account.id !== accountId));
      setSuccessMessage(`Successfully disconnected ${email} (${service})`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect Google account');
    }
  };


  const getConnectedAccountsByService = (serviceName: string): GoogleAccount[] => {
    return accounts.filter(account => account.service === serviceName);
  };

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Google Integration</h1>
        <p className="text-gray-600 mt-2">Connect your Google accounts to access Gmail and Calendar features</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div className="ml-3">
              <p className="text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Connection Cards */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Connect Services</h2>
          <div className="space-y-4">
            {GOOGLE_SERVICES.map((service) => {
              const connectedAccounts = getConnectedAccountsByService(service.name);
              const isConnecting = connectingService === service.name;

              return (
                <div key={service.name} className="card p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{service.icon}</div>
                      <div>
                        <h3 className="font-medium text-gray-900">{service.displayName}</h3>
                        <p className="text-sm text-gray-600">{service.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {connectedAccounts.length > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Connected ({connectedAccounts.length})
                        </span>
                      )}
                      <button
                        onClick={() => handleConnect(service.name)}
                        disabled={isConnecting}
                        className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isConnecting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Connecting...
                          </>
                        ) : (
                          'Connect'
                        )}
                      </button>
                    </div>
                  </div>

                  {connectedAccounts.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">Connected Accounts:</p>
                      <div className="space-y-2">
                        {connectedAccounts.map((account) => (
                          <div key={account.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">{account.email}</span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${new Date(account.expires_at || '') > new Date()
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {new Date(account.expires_at || '') > new Date() ? 'Active' : 'Expired'}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDisconnect(account.id, account.email, account.service)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Disconnect
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Connected Accounts Summary */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Connected Accounts</h2>
          {accounts.length === 0 ? (
            <div className="card p-6 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
              </div>
              <p className="text-gray-600">No Google accounts connected yet.</p>
              <p className="text-sm text-gray-500 mt-1">Connect your Gmail or Calendar accounts to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {GOOGLE_SERVICES.map((service) => {
                const serviceAccounts = getConnectedAccountsByService(service.name);
                if (serviceAccounts.length === 0) return null;

                return (
                  <div key={service.name} className="card p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <span className="text-2xl">{service.icon}</span>
                      <div>
                        <h3 className="font-medium text-gray-900">{service.displayName}</h3>
                        <p className="text-sm text-gray-600">{serviceAccounts.length} account(s) connected</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {serviceAccounts.map((account) => (
                        <div key={account.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border">
                              <span className="text-sm font-medium text-gray-700">
                                {account.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{account.email}</p>
                              <p className="text-xs text-gray-500">
                                Connected {account.created_at ? new Date(account.created_at).toLocaleDateString() : 'Unknown'}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${new Date(account.expires_at || '') > new Date()
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                              }`}>
                              {new Date(account.expires_at || '') > new Date() ? 'Active' : 'Expired'}
                            </span>
                            <button
                              onClick={() => handleDisconnect(account.id, account.email, account.service)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Disconnect
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
        <div className="prose prose-sm text-gray-600">
          <p>
            To connect your Google account, click the "Connect" button for the service you want to use.
            You'll be redirected to Google's authorization page where you can grant the necessary permissions.
          </p>
          <p className="mt-2">
            <strong>Permissions required:</strong>
          </p>
          <ul className="mt-1 ml-4 list-disc">
            <li>Gmail: Read-only access to your emails</li>
            <li>Calendar: Read and write access to your calendar events</li>
          </ul>
          <p className="mt-2">
            Your credentials are securely stored and encrypted. You can disconnect your accounts at any time.
          </p>
        </div>
      </div>
    </div>
  );
};
